// Session-replay trace for the Stateless Desktops demo.
//
// This is a MOCK trace with the exact schema the os-desktop benchmark harness
// should emit. To go live with real data, replace `statelessDemoTrace` with the
// harness's trace.json (same fields) and drop the boundary PNGs into
// public/demo/stateless/ (live-*.png, parked.png, wake-*.png), then set the
// matching `file` on each frame below. The SessionReplay component is fully
// data-driven, so no code changes are needed when the real trace lands.

export interface DemoStep {
  observeStart: number; // ms: agent starts reading the screen
  thinkStart: number;   // ms: model begins reasoning (host goes idle)
  parkStart: number;    // ms: VM snapshotted, host slot released
  parkDurable: number;  // ms: package demoted to object storage
  wakeStart: number;    // ms: restore begins
  wakeReady: number;    // ms: desktop interactive again
  act: number;          // ms: agent sends one input
}

export interface Sample { t: number; v: number }
export interface Frame { t: number; phase: 'live' | 'parked' | 'wake'; file?: string }

export interface StatelessDemoTrace {
  durationMs: number;
  hostResidentMaxMB: number;
  durableBytesMax: number;
  caption: string;
  steps: DemoStep[];
  hostResidentMB: Sample[]; // sampled host memory held by this desktop
  durableBytes: Sample[];   // bytes living in object storage (state off the host)
  frames: Frame[];          // desktop screenshots at boundaries
}

const RESIDENT_MB = 1024;
const DURABLE_BYTES = 223_000_000;

// Five parks, with the middle one stretched to five seconds. The short parks
// on either side carry the actual cadence (the desktop stops between every
// action); the long one in the middle is there so the gap, and the clock jump
// that follows it, are legible at a glance. Five seconds sits inside the
// measured think range: decision p50 1631 / p95 3686 ms, and 15125 ms at p95
// in the parked lane of the recorded Wordle run.
const steps: DemoStep[] = [
  { observeStart: 200,   thinkStart: 550,   parkStart: 700,   parkDurable: 850,   wakeStart: 1700,  wakeReady: 1800,  act: 2000 },
  { observeStart: 2350,  thinkStart: 2700,  parkStart: 2850,  parkDurable: 3000,  wakeStart: 3850,  wakeReady: 3950,  act: 4150 },
  { observeStart: 4500,  thinkStart: 4850,  parkStart: 5000,  parkDurable: 5150,  wakeStart: 10000, wakeReady: 10150, act: 10400 },
  { observeStart: 10750, thinkStart: 11100, parkStart: 11250, parkDurable: 11400, wakeStart: 12250, wakeReady: 12350, act: 12550 },
  { observeStart: 12900, thinkStart: 13250, parkStart: 13400, parkDurable: 13550, wakeStart: 14400, wakeReady: 14500, act: 14700 },
];

const DURATION = 15000;
const STEP_MS = 40;

// A desktop is resident on the host except during [parkStart, wakeStart); it
// ramps back to full between wakeStart and wakeReady.
function residentAt(t: number): number {
  for (const s of steps) {
    if (t >= s.parkStart && t < s.wakeStart) return 0;
    if (t >= s.wakeStart && t < s.wakeReady) {
      const frac = (t - s.wakeStart) / (s.wakeReady - s.wakeStart);
      return Math.round(RESIDENT_MB * frac);
    }
  }
  return RESIDENT_MB;
}

// State lives in object storage only while the desktop is parked.
function durableAt(t: number): number {
  for (const s of steps) {
    if (t >= s.parkDurable && t < s.wakeStart) return DURABLE_BYTES;
  }
  return 0;
}

function series(fn: (t: number) => number): Sample[] {
  const out: Sample[] = [];
  for (let t = 0; t <= DURATION; t += STEP_MS) out.push({ t, v: fn(t) });
  return out;
}

// Boundary frames. `file` is left undefined so the component renders its
// stylized fallback desktop; set these to the real PNG paths when captured.
const frames: Frame[] = [
  { t: 300,   phase: 'live',   /* file: '/demo/stateless/live-1.png' */ },
  { t: 900,   phase: 'parked', /* file: '/demo/stateless/parked.png' */ },
  { t: 1750,  phase: 'wake',   /* file: '/demo/stateless/wake-1.png' */ },
  { t: 2400,  phase: 'live',   /* file: '/demo/stateless/live-2.png' */ },
  { t: 3050,  phase: 'parked', /* file: '/demo/stateless/parked.png' */ },
  { t: 3900,  phase: 'wake',   /* file: '/demo/stateless/wake-2.png' */ },
  { t: 4550,  phase: 'live',   /* file: '/demo/stateless/live-3.png' */ },
  { t: 5200,  phase: 'parked', /* file: '/demo/stateless/parked.png' */ },
  { t: 10050, phase: 'wake',   /* file: '/demo/stateless/wake-3.png' */ },
  { t: 10800, phase: 'live',   /* file: '/demo/stateless/live-4.png' */ },
  { t: 11450, phase: 'parked', /* file: '/demo/stateless/parked.png' */ },
  { t: 12300, phase: 'wake',   /* file: '/demo/stateless/wake-4.png' */ },
  { t: 13600, phase: 'parked', /* file: '/demo/stateless/parked.png' */ },
  { t: 14350, phase: 'wake',   /* file: '/demo/stateless/wake-5.png' */ },
];

export const statelessDemoTrace: StatelessDemoTrace = {
  durationMs: DURATION,
  hostResidentMaxMB: RESIDENT_MB,
  durableBytesMax: DURABLE_BYTES,
  caption:
    'An illustration of time synchronization for a restored desktop.',
  steps,
  hostResidentMB: series(residentAt),
  durableBytes: series(durableAt),
  frames,
};
